import 'keen-slider/keen-slider.min.css'

import React, {useCallback, useEffect, useState} from 'react'
import {cx} from '@emotion/css'
import {useKeenSlider} from 'keen-slider/react'
import {motion} from 'motion/react'

import {Button} from '@vio/atlas/Button'
import {Box} from '@vio/atlas/helpers/Box'
import {Icon} from '@vio/atlas/Icon'
import {Image, SIZES} from '@vio/atlas/Image'
import {Text} from '@vio/atlas/Text'
import {useDispatchTeamEvent} from '@vio/core/src/analytics/components/TeamContext'
import {trackEvent} from '@vio/core/src/analytics/modules/actions'
import {Action, Category, Entity} from '@vio/core/src/analytics/types/Events'
import {ImageProvider} from '@vio/core/src/staticContent/types/ImageProvider'

import {Swiper} from '../../search/Swiper'
import {GallerySingleLoading} from './GallerySingleLoading'
import {
  BlurBackground,
  CarouselButtonWrapper,
  CarouselContainer,
  CarouselWrapper,
  ImageDetailsWrapper,
  SingleImagePanel,
  SliderWrapper,
  ThumbnailImageWrapper,
  ThumbnailsSection
} from './styles'
import {findImageIndexWithDefault} from './utils'

const CAROUSEL_LOADING = 100

export type CarouselActionType = 'NEXT' | 'PREV'

interface Props {
  images: string[]
  imageProvider: ImageProvider
  selectedImage?: string
  isLoading?: boolean
  selectedCategory?: string
  showImageDetails?: boolean
  blurBackground?: boolean
  component?: Entity
}

export const GallerySingleView: React.FC<Props> = ({
  images,
  imageProvider,
  isLoading,
  selectedImage,
  selectedCategory,
  showImageDetails = true,
  blurBackground = false,
  component = Entity.FullScreenImageGallery
}) => {
  const [imageIndex, setImageIndex] = useState<number>(
    findImageIndexWithDefault(images, selectedImage)
  )
  const [isLoaded, setLoaded] = useState(false)
  const [opacities, setOpacities] = React.useState<number[]>([])
  const dispatchTeamEvent = useDispatchTeamEvent()

  const handleScrollIntoView = useCallback((index: number) => {
    const element = document.getElementById(
      `image-${index === 0 ? index : index - 1}-thumbnail`
    )
    if (element) element.scrollIntoView({behavior: 'smooth', inline: 'center'})
    return
  }, [])

  const [sliderRef, sliderInstanceRef] = useKeenSlider<HTMLDivElement>({
    initial: findImageIndexWithDefault(images, selectedImage),
    slides: images.length,
    loop: true,
    slideChanged: slider => {
      const newIndex = slider.track.details.rel
      setImageIndex(newIndex)
      handleScrollIntoView(newIndex)
    },
    detailsChanged: changes => {
      const newOpacities = changes.track.details.slides.map(
        slide => slide.portion
      )
      setOpacities(newOpacities)
    },
    created: () => {
      setTimeout(() => setLoaded(true), CAROUSEL_LOADING)
    }
  })

  const handleArrowClick = useCallback(
    (direction: CarouselActionType) => {
      if (!isLoaded || !sliderInstanceRef?.current) return

      dispatchTeamEvent(
        trackEvent({
          category: Category.User,
          entity: component,
          action: Action.Scrolled,
          payload: {
            direction,
            selectedCategory
          }
        })
      )

      if (direction === 'PREV') {
        sliderInstanceRef.current.prev()
        return
      }
      sliderInstanceRef.current.next()
      return
    },
    [sliderInstanceRef, isLoaded, dispatchTeamEvent, selectedCategory]
  )

  const shouldLoadThisImage = useCallback(
    (imageIndex: number, selectedImage: number) => {
      switch (imageIndex) {
        case selectedImage:
        case selectedImage + 1:
        case selectedImage - 1:
        case images.length:
          return true
        default:
          return false
      }
    },
    [images]
  )

  const handleSelectThumbnail = useCallback(
    (newIndex: number) =>
      sliderInstanceRef?.current?.moveToIdx(newIndex, true, {duration: 0}),
    [sliderInstanceRef]
  )

  useEffect(() => {
    // Function to handle the keydown event
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'ArrowRight') {
        handleArrowClick('NEXT')
      } else if (event.key === 'ArrowLeft') {
        handleArrowClick('PREV')
      }
    }

    // Add the event listener to the window
    window.addEventListener('keydown', handleKeyDown)

    // Clean up the event listener when the component unmounts
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [handleArrowClick, isLoaded])

  useEffect(() => {
    setTimeout(() => setLoaded(true), CAROUSEL_LOADING)
  }, [])

  // Scroll to the selected image when the component mounts
  useEffect(() => {
    if (isLoaded) {
      const index = findImageIndexWithDefault(images, selectedImage)
      handleScrollIntoView(index)
    }
  }, [isLoaded, selectedImage, handleScrollIntoView, images])

  if (!isLoaded || isLoading) return <GallerySingleLoading />

  return (
    <Box
      display="flex"
      flexDirection="column"
      justifyContent="space-between"
      height="100%"
    >
      <SingleImagePanel>
        <motion.div
          initial={{opacity: 0}}
          animate={{opacity: 1}}
          transition={{duration: 0.2}}
          className="--motion-element"
        >
          <CarouselWrapper>
            <SliderWrapper ref={sliderRef} className="fader">
              {images.map((image, index) => (
                <CarouselContainer
                  opacity={opacities[index]}
                  className="fader__slide"
                  key={image}
                  data-testid={`${
                    index === imageIndex ? 'active-' : ''
                  }carousel-${index}`}
                >
                  {shouldLoadThisImage(index, imageIndex) && (
                    <>
                      {blurBackground && (
                        <BlurBackground
                          backgroundImage={imageProvider(image, SIZES['main'])}
                        />
                      )}
                      <Image
                        url={image}
                        isResponsive
                        backgroundSize="contain"
                        size="lightbox"
                        imageProvider={imageProvider}
                      />
                    </>
                  )}
                </CarouselContainer>
              ))}
            </SliderWrapper>

            {images.length > 1 && (
              <React.Fragment>
                <CarouselButtonWrapper
                  className={cx('--prev-button', '--new-gallery')}
                  onClick={() => handleArrowClick('PREV')}
                >
                  <Button
                    variant="tertiary"
                    iconStart={<Icon name="ChevronLeft" />}
                    isRounded
                  />
                </CarouselButtonWrapper>
                <CarouselButtonWrapper
                  onClick={() => handleArrowClick('NEXT')}
                  className="--next-button"
                >
                  <Button
                    variant="tertiary"
                    iconStart={<Icon name="ChevronRight" />}
                    isRounded
                  />
                </CarouselButtonWrapper>
              </React.Fragment>
            )}
          </CarouselWrapper>
        </motion.div>
      </SingleImagePanel>
      <Box display="flex" flexDirection="column">
        {showImageDetails && (
          <ImageDetailsWrapper
            className={selectedCategory ? '--has-category' : ''}
          >
            {selectedCategory && (
              <Text variant="bodyM">{selectedCategory}</Text>
            )}
            <Text variant="bodyS" align="center">
              {imageIndex + 1}/{images.length}
            </Text>
          </ImageDetailsWrapper>
        )}
        {images.length > 1 && (
          <ThumbnailsSection>
            <Swiper
              gap="s300"
              right={0}
              left={0}
              shadowWidth={60}
              buttonSize="md"
            >
              <Box display="flex" gap="s400" alignItems="center" padding="s500">
                {images.map((image, index) => (
                  <ThumbnailImageWrapper
                    key={image}
                    id={`image-${index}-thumbnail`}
                    className={cx(index === imageIndex && '--is-selected')}
                    onClick={() => handleSelectThumbnail(index)}
                    data-testid={`${
                      index === imageIndex ? 'active-' : ''
                    }thumbnail-${index}`}
                  >
                    <Image
                      borderRadius="none"
                      url={image}
                      imageProvider={imageProvider}
                    />
                  </ThumbnailImageWrapper>
                ))}
              </Box>
            </Swiper>
          </ThumbnailsSection>
        )}
      </Box>
    </Box>
  )
}
